import { Badge, Box, Flex, Group, Text, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';

import { DutiesAndTaxesTaxType, PredictedDistanceWithDutiesAndAntiDumping } from '@e-origin/shared';

import { arrowDownIcon, arrowUpIcon } from '@assets';
import { HS_CODE_POSITION_INDEX, HS_CODE_TARIC_SUBSTRING_LIMIT } from '@constants';
import { toDecimal } from '@utils';
import { riskAnalysisService } from '../../../../../services';
import classes from './HSCodeRecommendation.module.css';

export interface HSCodeRecommendationValue {
  hsCode: number;
  description: string;
  occurance: number;
  probability: number;
}

interface HSCodeRecommendationProps {
  value: PredictedDistanceWithDutiesAndAntiDumping;
  expanded?: boolean;
  onClick?: (value: PredictedDistanceWithDutiesAndAntiDumping | null) => void;
  'data-pw'?: number;
}

export const HSCodeRecommendation = (props: HSCodeRecommendationProps) => {
  const [taricDescriptionList, setTaricDescriptionList] = useState([]);

  useEffect(() => {
    if (props.expanded) {
      riskAnalysisService.retrieveTaricDescriptions(props.value.hsCode.toString()).then((data) => {
        setTaricDescriptionList(data.descriptionList);
      });
    } else {
      setTaricDescriptionList([]);
    }
  }, []);

  return (
    <Box className={props.expanded ? `${classes.container} ${classes.expanded}` : classes.container}>
      <Flex justify={'space-between'} align={'center'}>
        <UnstyledButton
          fz={18}
          fw={600}
          data-pw={`hsCode-recommendation-${props['data-pw']}`}
          onClick={(event) => {
            event.preventDefault();
            window.open(`https://eservices.minfin.fgov.be/extTariffBrowser/browseNomen.xhtml?cnCode=${props.value.hsCode}&lang=EN&page=1`, '_blank');
          }}
        >
          {props.value.hsCode}
        </UnstyledButton>
        <Flex>
          <Text mr={2} fz={16} fw={500} c={'#cd350c'}>
            {props.value.probability.toFixed(HS_CODE_TARIC_SUBSTRING_LIMIT)}%
          </Text>
          ({props.value.numberOfOccurances})
          <UnstyledButton
            ml={15}
            onClick={async ($event) => {
              $event.stopPropagation();
              props.onClick(!props.expanded ? props.value : null);
            }}
          >
            <img src={props.expanded ? arrowUpIcon : arrowDownIcon} alt="" width={18} height="auto" />
          </UnstyledButton>
        </Flex>
      </Flex>
      <Group gap="xs" mt={4}>
        {Object.entries(props.value.duties).map(([dutyType, duties]) => {
          const taxType = [DutiesAndTaxesTaxType.DUTY_A0, DutiesAndTaxesTaxType.VAT_B0].includes(dutyType as DutiesAndTaxesTaxType) ? dutyType + '0' : dutyType;
          return (
            <Group key={taxType} gap={4}>
              <Text m={0} size="sm" c="blue">
                {taxType}:
              </Text>
              <Text size="sm" c="blue">
                {duties.map((duty) => `${toDecimal(parseFloat(duty), 1)}%`).join(', ')};
              </Text>
            </Group>
          );
        })}
      </Group>
      {props.value.antiDumping && (
        <Badge data-pw={`${props['data-pw']}-badge`} color={'red'} size="xs" className={classes.badge}>
          Anti-dumping
        </Badge>
      )}
      {props.expanded && (
        <Box className={classes.hsCodeDescriptionContainer}>
          <Text display={'inline'} fz={13} fw={600}>
            Description:
          </Text>
          <Text className={classes.hsCodeDescription}>{props.value.description}</Text>
          {taricDescriptionList.map((description, idx) => (
            <>
              <Text className={classes.taricTitle}>{description[0].substr(0, parseInt(description[HS_CODE_POSITION_INDEX], 10))}</Text>
              <Text className={classes.taricDescription}>{description[HS_CODE_TARIC_SUBSTRING_LIMIT]}</Text>
            </>
          ))}
        </Box>
      )}
    </Box>
  );
};
